import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import {
  PortalInvoice,
  PortalPaymentMethod,
  PortalSubscription,
  usePortalData,
  usePortalInvoices,
  useCreateSetupIntent,
  useSetDefaultPaymentMethod,
  useCancelSubscription,
  useRequestPortalAccess,
} from "../data/portal";
import { getContrastColor } from "../utils/colors";
import { formatCurrency, formatDate } from "../utils/format";
import Header from "../components/header";

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const DEFAULT_BRAND_COLOR = "#FF8D27";
const DEFAULT_HEADER_COLOR = "#6B7280"; // Muted gray fallback for header

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getCardBrandIcon(brand: string): string {
  const icons: Record<string, string> = {
    visa: "https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg",
    mastercard:
      "https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg",
    amex: "https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg",
    discover:
      "https://js.stripe.com/v3/fingerprinted/img/discover-ac52cd46f89fa40a29a0bfb954e33173.svg",
  };
  return icons[brand.toLowerCase()] || "";
}

// Add Payment Method Form
function AddPaymentMethodForm({
  onSuccess,
  onCancel,
  brandColor,
}: {
  onSuccess: () => void;
  onCancel: () => void;
  brandColor: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    const { error: submitError } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (submitError) {
      setError(submitError.message || t("gymkeePay.portal.errorOccurred"));
      setIsProcessing(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement />
      {error && (
        <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700 border border-red-100">
          {error}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 cursor-pointer rounded-xl border-2 border-gray-200 px-5 py-3.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 hover:border-gray-300 active:scale-[0.98]"
        >
          {t("gymkeePay.portal.cancel")}
        </button>
        <button
          type="submit"
          disabled={isProcessing || !stripe}
          className="flex-1 cursor-pointer rounded-xl px-5 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          style={{
            backgroundColor: brandColor,
            boxShadow: `0 4px 14px ${hexToRgba(brandColor, 0.4)}`,
          }}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t("gymkeePay.portal.processing")}
            </span>
          ) : (
            t("gymkeePay.portal.addCard")
          )}
        </button>
      </div>
    </form>
  );
}

// Subscription Card
function SubscriptionCard({
  subscription,
  brandColor,
  onCancel,
  supportEmail,
}: {
  subscription: PortalSubscription;
  brandColor: string;
  onCancel: (id: string) => void;
  supportEmail?: string;
}) {
  const { t } = useTranslation();
  const isActive =
    subscription.status === "active" || subscription.status === "trialing";
  const isCanceling = subscription.cancelAtPeriodEnd;
  const isInCommitmentPeriod =
    subscription.commitmentEndDate &&
    new Date(subscription.commitmentEndDate) > new Date();

  const statusConfig: Record<
    string,
    { bg: string; text: string; label: string }
  > = {
    active: {
      bg: "#ecfdf5",
      text: "#059669",
      label: t("gymkeePay.portal.statusActive"),
    },
    trialing: {
      bg: "#eff6ff",
      text: "#2563eb",
      label: t("gymkeePay.portal.statusTrial"),
    },
    past_due: {
      bg: "#fef2f2",
      text: "#dc2626",
      label: t("gymkeePay.portal.statusPastDue"),
    },
    canceled: {
      bg: "#f9fafb",
      text: "#6b7280",
      label: t("gymkeePay.portal.statusCanceled"),
    },
  };

  const status = statusConfig[subscription.status] || statusConfig.canceled;

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm border border-gray-200">
      {/* Header - Name and Status */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-base font-semibold text-gray-900 leading-tight flex-1 min-w-0">
          {subscription.productName}
        </h3>
        <span
          className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold shrink-0"
          style={{ backgroundColor: status.bg, color: status.text }}
        >
          {isCanceling ? t("gymkeePay.portal.canceling") : status.label}
        </span>
      </div>

      {/* Price */}
      <div className="flex items-baseline gap-1 mb-3">
        <span className="text-xl font-bold" style={{ color: brandColor }}>
          {formatCurrency(
            subscription.price.amount,
            subscription.price.currency,
          )}
        </span>
        {subscription.price.interval && (
          <span className="text-xs text-gray-500">
            /{" "}
            {subscription.price.intervalCount &&
            subscription.price.intervalCount > 1
              ? `${subscription.price.intervalCount} `
              : ""}
            {t(`gymkeePay.interval.${subscription.price.interval}`, { defaultValue: subscription.price.interval })}
          </span>
        )}
      </div>

      {/* Details */}
      <div className="space-y-1.5 text-xs">
        {subscription.currentPeriodEnd && (
          <div className="flex items-center justify-between text-gray-600">
            <span>
              {isCanceling
                ? t("gymkeePay.portal.accessUntil")
                : t("gymkeePay.portal.nextBilling")}
            </span>
            <span className="font-medium text-gray-900">
              {formatDate(subscription.currentPeriodEnd)}
            </span>
          </div>
        )}

        {isInCommitmentPeriod && (
          <div className="flex items-center justify-between text-amber-600">
            <span>{t("gymkeePay.portal.commitmentUntil")}</span>
            <span className="font-medium">
              {formatDate(subscription.commitmentEndDate)}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      {isActive && !isCanceling && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          {isInCommitmentPeriod ? (
            <p className="text-xs text-gray-500">
              {t("gymkeePay.portal.contactCoachToCancel")}{" "}
              {supportEmail && (
                <a
                  href={`mailto:${supportEmail}`}
                  className="font-medium hover:underline break-all"
                  style={{ color: brandColor }}
                >
                  {supportEmail}
                </a>
              )}
            </p>
          ) : (
            <button
              onClick={() => onCancel(subscription.id)}
              className="cursor-pointer text-xs text-gray-400 hover:text-red-500 transition-colors"
            >
              {t("gymkeePay.portal.cancelSubscription")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Payment Method Card
function PaymentMethodCard({
  paymentMethod,
  brandColor,
  onSetDefault,
  isSettingDefault,
}: {
  paymentMethod: PortalPaymentMethod;
  brandColor: string;
  onSetDefault: (id: string) => void;
  isSettingDefault: boolean;
}) {
  const { t } = useTranslation();
  const brandIcon = getCardBrandIcon(paymentMethod.card?.brand || "");
  const isDefault = paymentMethod.isDefault;

  return (
    <div
      className={`rounded-xl bg-white p-3 border ${
        isDefault ? "border-2" : "border-gray-200"
      }`}
      style={isDefault ? { borderColor: brandColor } : {}}
    >
      {/* Row layout */}
      <div className="flex items-center">
        {/* Card Icon */}
        <div className="w-12 h-8 flex items-center justify-center bg-gray-50 rounded mr-3">
          {brandIcon ? (
            <img
              src={brandIcon}
              alt={paymentMethod.card?.brand}
              className="h-5"
            />
          ) : (
            <svg
              className="h-4 w-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"
              />
            </svg>
          )}
        </div>

        {/* Card Info */}
        <div className="flex-1">
          <span className="text-sm font-medium text-gray-900">
            •••• {paymentMethod.card?.last4}
          </span>
          <span className="text-xs text-gray-400 ml-2">
            {paymentMethod.card?.expMonth?.toString().padStart(2, "0")}/
            {paymentMethod.card?.expYear}
          </span>
        </div>

        {/* Badge or Button */}
        {isDefault ? (
          <span
            className="text-[10px] font-semibold text-white px-2 py-1 rounded-full ml-2"
            style={{ backgroundColor: brandColor }}
          >
            {t("gymkeePay.portal.default")}
          </span>
        ) : (
          <button
            onClick={() => onSetDefault(paymentMethod.id)}
            disabled={isSettingDefault}
            className="text-xs text-gray-500 hover:text-gray-700 ml-2 cursor-pointer"
          >
            {t("gymkeePay.portal.setDefault")}
          </button>
        )}
      </div>
    </div>
  );
}

// Invoice Row
function InvoiceRow({
  invoice,
  brandColor,
}: {
  invoice: PortalInvoice;
  brandColor: string;
}) {
  const { t } = useTranslation();

  const statusConfig: Record<
    string,
    { bg: string; text: string; label: string }
  > = {
    paid: {
      bg: "#dcfce7",
      text: "#166534",
      label: t("gymkeePay.portal.invoicePaid"),
    },
    open: {
      bg: "#fef3c7",
      text: "#b45309",
      label: t("gymkeePay.portal.invoiceOpen"),
    },
    draft: {
      bg: "#f3f4f6",
      text: "#6b7280",
      label: t("gymkeePay.portal.invoiceDraft"),
    },
    void: {
      bg: "#f3f4f6",
      text: "#6b7280",
      label: t("gymkeePay.portal.invoiceVoid"),
    },
    uncollectible: {
      bg: "#fee2e2",
      text: "#b91c1c",
      label: t("gymkeePay.portal.invoiceUncollectible"),
    },
  };

  const status = statusConfig[invoice.status] || statusConfig.draft;

  return (
    <div className="flex items-center gap-2 py-2.5 border-b border-gray-100 last:border-0">
      {/* Left: Invoice info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">
            {invoice.number || `#${invoice.id.slice(0, 8).toUpperCase()}`}
          </p>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-semibold shrink-0"
            style={{ backgroundColor: status.bg, color: status.text }}
          >
            {status.label}
          </span>
        </div>
        <p className="text-xs text-gray-500">{formatDate(invoice.createdOn)}</p>
      </div>

      {/* Right: Amount and download */}
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm font-semibold text-gray-900">
          {formatCurrency(invoice.total, invoice.currency)}
        </span>
        {invoice.invoicePdf && (
          <a
            href={invoice.invoicePdf}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            style={{ color: brandColor }}
            title={t("gymkeePay.portal.downloadPdf", { defaultValue: "Download PDF" })}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
              />
            </svg>
          </a>
        )}
      </div>
    </div>
  );
}

// Section Component
function Section({
  title,
  action,
  brandColor,
  children,
}: {
  title: string;
  action?: { label: string; onClick: () => void };
  brandColor: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {action && (
          <button
            onClick={action.onClick}
            className="flex cursor-pointer items-center gap-1 text-xs font-medium transition-colors hover:opacity-80"
            style={{ color: brandColor }}
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
            {action.label}
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

// Empty State
function EmptyState({
  text,
  action,
  brandColor,
}: {
  text: string;
  action?: { label: string; onClick: () => void };
  brandColor: string;
}) {
  return (
    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center">
      <p className="text-sm text-gray-500">{text}</p>
      {action && (
        <button
          onClick={action.onClick}
          className="mt-3 cursor-pointer rounded-lg px-4 py-2 text-xs font-medium text-white transition-colors hover:opacity-90"
          style={{ backgroundColor: brandColor }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

export default function CustomerPortal() {
  const { stripeCustomerId } = useParams<{ stripeCustomerId: string }>();
  const [searchParams] = useSearchParams();
  const stripeAccountId = searchParams.get("stripeAccountId") || "";
  const token = searchParams.get("token") || "";
  const action = searchParams.get("action");
  const { t } = useTranslation();

  // Request access mutation (for when no token is present)
  const requestAccessMutation = useRequestPortalAccess();
  const [accessRequested, setAccessRequested] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);

  // TanStack Query hooks (only enabled when token is present)
  const {
    data: portalData,
    isLoading: isLoadingPortal,
    isError: isPortalError,
  } = usePortalData(stripeCustomerId, stripeAccountId, token || undefined);

  const { data: invoicesData, isLoading: isLoadingInvoices } =
    usePortalInvoices(stripeCustomerId, stripeAccountId, token || undefined);

  const createSetupIntentMutation = useCreateSetupIntent();
  const setDefaultPaymentMethodMutation = useSetDefaultPaymentMethod(
    stripeCustomerId,
    stripeAccountId,
    token || undefined,
  );
  const cancelSubscriptionMutation = useCancelSubscription(
    stripeCustomerId,
    stripeAccountId,
    token || undefined,
  );

  // Local state for Stripe and UI
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(
    null,
  );
  const [showCancelConfirm, setShowCancelConfirm] = useState<string | null>(
    null,
  );
  // Surfaces mutation failures (add payment method, set default, cancel)
  // to the user instead of silently swallowing them.
  const [actionError, setActionError] = useState<string | null>(null);

  const invoices = invoicesData?.invoices || [];
  const brandColor = portalData?.business.brandColor || DEFAULT_BRAND_COLOR;
  const headerColor = portalData?.business.brandColor || DEFAULT_HEADER_COLOR;
  const textOnBrand = getContrastColor(headerColor);

  // Load Stripe when portal data is available
  useEffect(() => {
    if (portalData?.stripeAccountId) {
      loadStripe(STRIPE_KEY, {
        stripeAccount: portalData.stripeAccountId,
      }).then(setStripeInstance);
    }
  }, [portalData?.stripeAccountId]);

  // Handle action query param (e.g., ?action=update-payment)
  useEffect(() => {
    if (
      action === "update-payment" &&
      portalData &&
      !showAddPayment &&
      !setupClientSecret
    ) {
      handleAddPaymentMethod();
    }
  }, [action, portalData, showAddPayment, setupClientSecret]);

  const handleAddPaymentMethod = async () => {
    if (!stripeCustomerId || !stripeAccountId || !token) return;
    setActionError(null);
    try {
      const result = await createSetupIntentMutation.mutateAsync({
        stripeCustomerId,
        stripeAccountId,
        token,
      });
      setSetupClientSecret(result.clientSecret);
      setShowAddPayment(true);
    } catch (err) {
      console.error("Failed to create setup intent:", err);
      setActionError(t("gymkeePay.portal.errorOccurred"));
    }
  };

  const handleRequestAccess = async () => {
    if (!stripeCustomerId || !stripeAccountId) return;
    try {
      const result = await requestAccessMutation.mutateAsync({
        stripeCustomerId,
        stripeAccountId,
      });
      setAccessRequested(true);
      setMaskedEmail(result.email);
    } catch (err) {
      console.error("Failed to request access:", err);
    }
  };

  const handlePaymentMethodAdded = () => {
    setShowAddPayment(false);
    setSetupClientSecret(null);
  };

  const handleSetDefault = (paymentMethodId: string) => {
    setActionError(null);
    setDefaultPaymentMethodMutation.mutate(paymentMethodId, {
      onError: (err) => {
        console.error("Failed to set default payment method:", err);
        setActionError(t("gymkeePay.portal.errorOccurred"));
      },
    });
  };

  const handleCancelSubscription = (subscriptionId: string) => {
    setActionError(null);
    cancelSubscriptionMutation.mutate(subscriptionId, {
      onSuccess: () => {
        setShowCancelConfirm(null);
      },
      onError: (err) => {
        console.error("Failed to cancel subscription:", err);
        setShowCancelConfirm(null);
        setActionError(t("gymkeePay.portal.errorOccurred"));
      },
    });
  };

  const isLoading = isLoadingPortal || isLoadingInvoices;
  const hasError = isPortalError || !stripeCustomerId || !stripeAccountId;

  // No token - request access flow
  if (!token && stripeCustomerId && stripeAccountId) {
    return (
      <div
        className="flex min-h-screen items-center justify-center p-4"
        style={{ backgroundColor: "#f8fafc" }}
      >
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
          {accessRequested ? (
            <>
              <div
                className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full"
                style={{
                  backgroundColor: hexToRgba(DEFAULT_BRAND_COLOR, 0.15),
                }}
              >
                <svg
                  className="h-8 w-8"
                  style={{ color: DEFAULT_BRAND_COLOR }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h1 className="mb-2 text-xl font-bold text-gray-900">
                {t("gymkeePay.portal.checkYourEmail")}
              </h1>
              <p className="text-gray-600">
                {t("gymkeePay.portal.accessLinkSent", { email: maskedEmail })}
              </p>
            </>
          ) : (
            <>
              <div
                className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full"
                style={{
                  backgroundColor: hexToRgba(DEFAULT_BRAND_COLOR, 0.15),
                }}
              >
                <svg
                  className="h-8 w-8"
                  style={{ color: DEFAULT_BRAND_COLOR }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h1 className="mb-2 text-xl font-bold text-gray-900">
                {t("gymkeePay.portal.verificationRequired")}
              </h1>
              <p className="mb-6 text-gray-600">
                {t("gymkeePay.portal.verificationMessage")}
              </p>
              <button
                onClick={handleRequestAccess}
                disabled={requestAccessMutation.isPending}
                className="w-full cursor-pointer rounded-xl px-5 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:opacity-90 hover:shadow-xl active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: DEFAULT_BRAND_COLOR,
                  boxShadow: `0 4px 14px ${hexToRgba(DEFAULT_BRAND_COLOR, 0.4)}`,
                }}
              >
                {requestAccessMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {t("gymkeePay.portal.sending")}
                  </span>
                ) : (
                  t("gymkeePay.portal.sendAccessLink")
                )}
              </button>
              {requestAccessMutation.isError && (
                <p className="mt-4 text-sm text-red-600">
                  {t("gymkeePay.portal.accessRequestFailed")}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "#f8fafc" }}>
        <div className="relative" style={{ backgroundColor: "#f1f1f1" }}>
          <div className="px-4 py-12 sm:py-16">
            <div className="mx-auto max-w-xl text-center">
              <div className="mx-auto mb-4 h-20 w-20 animate-pulse rounded-full bg-white/20" />
              <div className="mx-auto mb-2 h-7 w-40 animate-pulse rounded-lg bg-white/20" />
              <div className="mx-auto h-5 w-56 animate-pulse rounded-lg bg-white/20" />
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-xl px-4 py-8 -mt-8">
          <div className="space-y-6">
            <div className="h-48 animate-pulse rounded-2xl bg-white shadow-lg" />
            <div className="h-32 animate-pulse rounded-2xl bg-white shadow-lg" />
          </div>
        </div>
      </div>
    );
  }

  // Error
  if (hasError || !portalData) {
    return (
      <div
        className="flex min-h-screen items-center justify-center p-4"
        style={{ backgroundColor: brandColor }}
      >
        <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-xl font-bold text-gray-900">
            {t("gymkeePay.portal.error")}
          </h1>
          <p className="text-gray-600">
            {!stripeCustomerId || !stripeAccountId
              ? t("gymkeePay.portal.missingInformation")
              : t("gymkeePay.portal.failedToLoadAccount")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f8fafc" }}>
      {/* Top Header - Business info */}
      <div
        className="relative"
        style={{ backgroundColor: headerColor, minHeight: "80px" }}
      >
        <Header
          businessName={portalData.business.name}
          textColor={textOnBrand}
        />
      </div>

      {/* User Info Section */}
      <div className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="mx-auto max-w-md flex items-center gap-4">
          {/* User Avatar */}
          {portalData.customer.photo ? (
            <img
              src={portalData.customer.photo}
              alt={portalData.customer.name || ""}
              className="h-14 w-14 rounded-full object-cover border-2 border-gray-100"
            />
          ) : (
            <div className="h-14 w-14 rounded-full flex items-center justify-center text-xl font-bold bg-gray-100 text-gray-500">
              {portalData.customer.name?.charAt(0) ||
                portalData.customer.email?.charAt(0)?.toUpperCase() ||
                "?"}
            </div>
          )}
          {/* User Details */}
          <div className="flex-1 min-w-0">
            {portalData.customer.name && (
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {portalData.customer.name}
              </h2>
            )}
            <p className="text-sm text-gray-500 truncate">
              {portalData.customer.email}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-md px-4 py-5">
        {/* Action error banner */}
        {actionError && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
            <span>{actionError}</span>
            <button
              type="button"
              onClick={() => setActionError(null)}
              className="shrink-0 cursor-pointer text-red-400 transition-colors hover:text-red-600"
              aria-label={t("gymkeePay.portal.cancel")}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Subscriptions */}
        <Section
          title={t("gymkeePay.portal.subscriptions")}
          brandColor={brandColor}
        >
          {portalData.subscriptions.length === 0 ? (
            <EmptyState
              text={t("gymkeePay.portal.noSubscriptions")}
              brandColor={brandColor}
            />
          ) : (
            <div className="space-y-3">
              {portalData.subscriptions.map((sub) => (
                <SubscriptionCard
                  key={sub.id}
                  subscription={sub}
                  brandColor={brandColor}
                  onCancel={(id) => setShowCancelConfirm(id)}
                  supportEmail={portalData.business.supportEmail}
                />
              ))}
            </div>
          )}
        </Section>

        {/* Payment Methods */}
        <Section
          title={t("gymkeePay.portal.paymentMethods")}
          action={
            !showAddPayment
              ? {
                  label: t("gymkeePay.portal.addPaymentMethod"),
                  onClick: handleAddPaymentMethod,
                }
              : undefined
          }
          brandColor={brandColor}
        >
          {showAddPayment && setupClientSecret && stripeInstance ? (
            <div className="rounded-xl bg-white border border-gray-200 p-4">
              <Elements
                stripe={stripeInstance}
                options={{
                  clientSecret: setupClientSecret,
                  appearance: {
                    theme: "stripe",
                    variables: {
                      colorPrimary: brandColor,
                      borderRadius: "12px",
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    },
                  },
                }}
              >
                <AddPaymentMethodForm
                  onSuccess={handlePaymentMethodAdded}
                  onCancel={() => {
                    setShowAddPayment(false);
                    setSetupClientSecret(null);
                  }}
                  brandColor={brandColor}
                />
              </Elements>
            </div>
          ) : portalData.paymentMethods.length === 0 ? (
            <EmptyState
              text={t("gymkeePay.portal.noPaymentMethods")}
              action={{
                label: t("gymkeePay.portal.addPaymentMethod"),
                onClick: handleAddPaymentMethod,
              }}
              brandColor={brandColor}
            />
          ) : (
            <div className="space-y-3">
              {/* Show default card first */}
              {portalData.paymentMethods
                .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
                .map((pm) => (
                  <PaymentMethodCard
                    key={pm.id}
                    paymentMethod={pm}
                    brandColor={brandColor}
                    onSetDefault={handleSetDefault}
                    isSettingDefault={setDefaultPaymentMethodMutation.isPending}
                  />
                ))}
            </div>
          )}
        </Section>

        {/* Invoices */}
        <Section
          title={t("gymkeePay.portal.invoiceHistory")}
          brandColor={brandColor}
        >
          {invoices.length === 0 ? (
            <EmptyState
              text={t("gymkeePay.portal.noInvoices")}
              brandColor={brandColor}
            />
          ) : (
            <div className="rounded-xl bg-white border border-gray-200 px-3 py-1">
              {invoices.map((invoice) => (
                <InvoiceRow
                  key={invoice.id}
                  invoice={invoice}
                  brandColor={brandColor}
                />
              ))}
            </div>
          )}
        </Section>

        {/* Footer */}
        <footer className="mt-8 pb-8 text-center">
          <p className="text-xs text-gray-500">
            {t("gymkeePay.portal.questions")}{" "}
            <a
              href={`mailto:${portalData.business.supportEmail}`}
              className="font-medium hover:underline break-all"
              style={{ color: brandColor }}
            >
              {portalData.business.supportEmail}
            </a>
          </p>
          <div className="mt-6 flex flex-col items-center gap-3">
            <p className="text-xs text-gray-400">
              {t("gymkeePay.portal.poweredBy")}
            </p>
            <a
              href="https://gymkee.com"
              target="_blank"
              rel="noopener noreferrer"
              className="opacity-60 hover:opacity-100 transition-opacity"
            >
              <svg
                width="100"
                height="24"
                viewBox="0 0 516 124"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M63.7199 30.2198C70.8006 38.8463 74.6835 49.6573 74.7106 60.821C74.666 67.242 72.8747 73.5296 69.5293 79.0089C68.2793 81.0166 66.8044 82.8749 65.133 84.5477C58.4137 90.8422 49.6416 94.4849 40.4432 94.8004C27.0974 95.2325 15.6749 91.4614 7.00011 80.3837C-5.95321 63.8457 -1.1644 31.9482 23.3292 24.2095C23.8787 24.2095 24.4674 23.8167 25.3702 23.581C29.8065 22.2965 34.4133 21.7004 39.0302 21.8133C56.3012 22.049 64.9368 22.1668 65.8788 25.1916C66.1143 26.1344 65.9573 27.7057 63.7199 30.2198ZM18.4226 57.7177C18.4847 62.5867 20.4745 67.2322 23.9553 70.6347C27.4361 74.0372 32.1236 75.9186 36.989 75.8662C39.438 76.0213 41.8931 75.6795 44.2068 74.8613C46.5206 74.0432 48.6452 72.7655 50.4532 71.1051C52.2613 69.4447 53.7153 67.4357 54.7282 65.1989C55.7411 62.9621 56.2919 60.5434 56.3475 58.0883C56.403 55.6331 55.9623 53.192 55.0516 50.9115C54.141 48.6311 52.7793 46.5583 51.0483 44.8177C49.3173 43.077 47.2525 41.7044 44.9782 40.7822C42.7039 39.86 40.2668 39.4073 37.8133 39.4512C35.3054 39.295 32.7922 39.6565 30.4298 40.5131C28.0673 41.3698 25.9061 42.7034 24.0803 44.4312C22.2545 46.1589 20.8032 48.2438 19.8164 50.5565C18.8296 52.8691 18.3285 55.3601 18.3441 57.8748L18.4226 57.7177Z"
                  fill="#FF8D27"
                />
                <path
                  d="M49.3151 107.45C54.2138 106.015 58.8329 103.757 62.975 100.772C68.6666 96.6083 70.5115 92.9157 71.3358 93.4657C72.1601 94.0156 70.3152 107.254 61.0909 115.621C46.646 128.781 25.2534 121.867 23.6833 121.317C13.3206 117.821 5.07758 110.161 6.05889 108C7.0402 105.84 16.186 110.239 32.7898 109.689C38.3728 109.676 43.9295 108.923 49.3151 107.45Z"
                  fill="#FF8D27"
                />
                <path
                  d="M160.079 21.84L118.079 120.6H95.9991L111.359 86.52L84.1191 21.84H107.039L122.519 63.72L137.879 21.84H160.079Z"
                  fill="#FF8D27"
                />
                <path
                  d="M255.339 21.12C263.659 21.12 270.259 23.64 275.139 28.68C280.099 33.72 282.579 40.72 282.579 49.68V88.8H262.179V52.44C262.179 48.12 261.019 44.8 258.699 42.48C256.459 40.08 253.339 38.88 249.339 38.88C245.339 38.88 242.179 40.08 239.859 42.48C237.619 44.8 236.499 48.12 236.499 52.44V88.8H216.099V52.44C216.099 48.12 214.939 44.8 212.619 42.48C210.379 40.08 207.259 38.88 203.259 38.88C199.259 38.88 196.099 40.08 193.779 42.48C191.539 44.8 190.419 48.12 190.419 52.44V88.8H169.899V21.84H190.419V30.24C192.499 27.44 195.219 25.24 198.579 23.64C201.939 21.96 205.739 21.12 209.979 21.12C215.019 21.12 219.499 22.2 223.419 24.36C227.419 26.52 230.539 29.6 232.779 33.6C235.099 29.92 238.259 26.92 242.259 24.6C246.259 22.28 250.619 21.12 255.339 21.12Z"
                  fill="#FF8D27"
                />
                <path
                  d="M340.251 88.8L319.851 60.72V88.8H299.331V0H319.851V49.08L340.131 21.84H365.451L337.611 55.44L365.691 88.8H340.251Z"
                  fill="#FF8D27"
                />
                <path
                  d="M439.03 54.24C439.03 56.16 438.91 58.16 438.67 60.24H392.23C392.55 64.4 393.87 67.6 396.19 69.84C398.59 72 401.51 73.08 404.95 73.08C410.07 73.08 413.63 70.92 415.63 66.6H437.47C436.35 71 434.31 74.96 431.35 78.48C428.47 82 424.83 84.76 420.43 86.76C416.03 88.76 411.11 89.76 405.67 89.76C399.11 89.76 393.27 88.36 388.15 85.56C383.03 82.76 379.03 78.76 376.15 73.56C373.27 68.36 371.83 62.28 371.83 55.32C371.83 48.36 373.23 42.28 376.03 37.08C378.91 31.88 382.91 27.88 388.03 25.08C393.15 22.28 399.03 20.88 405.67 20.88C412.15 20.88 417.91 22.24 422.95 24.96C427.99 27.68 431.91 31.56 434.71 36.6C437.59 41.64 439.03 47.52 439.03 54.24ZM418.03 48.84C418.03 45.32 416.83 42.52 414.43 40.44C412.03 38.36 409.03 37.32 405.43 37.32C401.99 37.32 399.07 38.32 396.67 40.32C394.35 42.32 392.91 45.16 392.35 48.84H418.03Z"
                  fill="#FF8D27"
                />
                <path
                  d="M515.376 54.24C515.376 56.16 515.256 58.16 515.016 60.24H468.576C468.896 64.4 470.216 67.6 472.536 69.84C474.936 72 477.856 73.08 481.296 73.08C486.416 73.08 489.976 70.92 491.976 66.6H513.816C512.696 71 510.656 74.96 507.696 78.48C504.816 82 501.176 84.76 496.776 86.76C492.376 88.76 487.456 89.76 482.016 89.76C475.456 89.76 469.616 88.36 464.496 85.56C459.376 82.76 455.376 78.76 452.496 73.56C449.616 68.36 448.176 62.28 448.176 55.32C448.176 48.36 449.576 42.28 452.376 37.08C455.256 31.88 459.256 27.88 464.376 25.08C469.496 22.28 475.376 20.88 482.016 20.88C488.496 20.88 494.256 22.24 499.296 24.96C504.336 27.68 508.256 31.56 511.056 36.6C513.936 41.64 515.376 47.52 515.376 54.24ZM494.376 48.84C494.376 45.32 493.176 42.52 490.776 40.44C488.376 38.36 485.376 37.32 481.776 37.32C478.336 37.32 475.416 38.32 473.016 40.32C470.696 42.32 469.256 45.16 468.696 48.84H494.376Z"
                  fill="#FF8D27"
                />
              </svg>
            </a>
          </div>
        </footer>
      </div>

      {/* Cancel Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowCancelConfirm(null)}
          />
          <div className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-white p-6 sm:p-8 shadow-2xl">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-7 w-7 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-center text-xl font-bold text-gray-900">
              {t("gymkeePay.portal.confirmCancel")}
            </h3>
            <p className="mb-6 text-center text-gray-600">
              {t("gymkeePay.portal.cancelWarning")}
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => setShowCancelConfirm(null)}
                className="flex-1 cursor-pointer rounded-xl border-2 border-gray-200 px-5 py-3.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 hover:border-gray-300 active:scale-[0.98]"
              >
                {t("gymkeePay.portal.keepSubscription")}
              </button>
              <button
                onClick={() => handleCancelSubscription(showCancelConfirm)}
                disabled={cancelSubscriptionMutation.isPending}
                className="flex-1 cursor-pointer rounded-xl bg-red-600 px-5 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:bg-red-700 hover:shadow-xl active:scale-[0.98] disabled:opacity-50"
                style={{ boxShadow: "0 4px 14px rgba(220, 38, 38, 0.4)" }}
              >
                {cancelSubscriptionMutation.isPending
                  ? t("gymkeePay.portal.processing")
                  : t("gymkeePay.portal.confirmCancelButton")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
